/**
 * Academic360 isolated API client.
 */
import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/education360/academic`;

const client = axios.create({ baseURL: BASE });
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("edu360.token");
  const tenant = localStorage.getItem("edu360.tenant_slug");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  if (tenant) cfg.headers["X-Tenant-Slug"] = tenant;
  return cfg;
});

export const acAPI = {
  // Curriculum & subjects
  curricula: () => client.get(`/curricula`),
  createCurriculum: (d) => client.post(`/curricula`, d),
  subjects: (params) => client.get(`/subjects`, { params }),
  createSubject: (d) => client.post(`/subjects`, d),
  updateSubject: (id, d) => client.patch(`/subjects/${id}`, d),

  // Schemes
  schemes: (subject_id) => client.get(`/schemes`, { params: { subject_id } }),
  upsertScheme: (d) => client.post(`/schemes`, d),

  // Lessons
  lessons: (params) => client.get(`/lessons`, { params }),
  createLesson: (d) => client.post(`/lessons`, d),
  updateLesson: (id, d) => client.patch(`/lessons/${id}`, d),

  // Homework
  homework: (params) => client.get(`/homework`, { params }),
  createHomework: (d) => client.post(`/homework`, d),
  submissions: (id) => client.get(`/homework/${id}/submissions`),
  markSubmission: (sid, d) => client.patch(`/homework/submissions/${sid}`, d),

  // Assessments
  assessments: (params) => client.get(`/assessments`, { params }),
  createAssessment: (d) => client.post(`/assessments`, d),
  markbook: (id) => client.get(`/assessments/${id}/markbook`),
  saveMarks: (id, marks) => client.post(`/assessments/${id}/marks/bulk`, { marks }),

  // Gradebook
  gradebookClass: (class_id, subject_id, academic_year, term) =>
    client.get(`/gradebook/class/${class_id}`, { params: { subject_id, academic_year, term } }),
  gradebookStudent: (student_id, params) => client.get(`/gradebook/student/${student_id}`, { params }),
  rebuildGradebook: (d) => client.post(`/gradebook/rebuild`, d),

  // Moderation
  submitModeration: (d) => client.post(`/moderation`, d),
  listModeration: (stage) => client.get(`/moderation`, { params: stage ? { stage } : {} }),
  advanceModeration: (id, d) => client.post(`/moderation/${id}/advance`, d),

  // Promotion
  runPromotion: (d) => client.post(`/promotion/run`, d),
  listPromotion: (params) => client.get(`/promotion`, { params }),
  approvePromotion: (id, d) => client.patch(`/promotion/${id}`, d),

  // Interventions
  interventions: (status) => client.get(`/interventions`, { params: status ? { status } : {} }),
  createIntervention: (d) => client.post(`/interventions`, d),
  updateIntervention: (id, d) => client.patch(`/interventions/${id}`, d),

  // Teacher workbench
  workbench: () => client.get(`/teacher/workbench`),

  // Analytics
  kpis: () => client.get(`/analytics/kpis`),
  atRisk: (threshold) => client.get(`/analytics/at-risk`, { params: threshold ? { threshold } : {} }),

  // AI
  aiStudent: (student_id) => client.post(`/ai/student/${student_id}`),
  aiClass: (d) => client.post(`/ai/class`, d),
  aiSubject: (d) => client.post(`/ai/subject`, d),
  aiExec: () => client.post(`/ai/executive-briefing`),
  aiAtRisk: () => client.post(`/ai/at-risk`),
  briefings: (kind) => client.get(`/ai/briefings`, { params: kind ? { kind } : {} }),

  // PDF URLs
  reportCardPdf: (student_id, academic_year, term) =>
    `${BASE}/reports/report-card/${student_id}.pdf?academic_year=${academic_year}&term=${term}`,
  transcriptPdf: (student_id) => `${BASE}/reports/transcript/${student_id}.pdf`,
  executivePdf: () => `${BASE}/reports/executive.pdf`,
};
